import React, { useContext } from 'react';
import { Box, Tooltip, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Close from '@mui/icons-material/Close';
import { ReactComponent as CopilotChatButton } from '../../svg/mindtickle/mt-buyer-copilot-chat-button.svg';
import { ReactComponent as ConfigureChatIcon } from '../../svg/mindtickle/mt-buyer-copilot-configure-chat.svg';
import { ReactComponent as ConfigureChatIconDisabled } from '../../svg/mindtickle/mt-buyer-copilot-chat-button-disabled.svg';
import { OrganizationThemeContext } from '../../lib/context/OrganizationThemeContext';
import { DRAWER_Z_INDEX } from '../../definitions/constants';

const useStyles = makeStyles((theme: Theme) => ({
  buttonContainer: {
    alignItems: 'center',
    borderRadius: theme.spacing(1.5),
    display: 'flex',
    height: theme.spacing(5.75),
    justifyContent: 'center',
    width: theme.spacing(5.75),
    cursor: 'pointer',
  },
}));

export interface BuyerCopilotButtonProps {
  disabled?: boolean;
  showCloseIcon?: boolean;
  isHovered?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  tooltipTitle?: string;
}

const BuyerCopilotButton: React.FC<BuyerCopilotButtonProps> = (
  props: BuyerCopilotButtonProps
) => {
  const {
    disabled = false,
    isHovered = false,
    onClick,
    onMouseEnter,
    onMouseLeave,
    tooltipTitle = '',
    showCloseIcon = false,
  } = props;

  const { buttonsStyles } = useContext(OrganizationThemeContext);

  const classes = useStyles();

  const renderChatIcon = () => {
    if (disabled) {
      return <ConfigureChatIconDisabled />;
    }
    if (isHovered && !disabled) {
      return <ConfigureChatIcon />;
    }
    if (showCloseIcon) {
      return <Close sx={{ color: 'var(--text-primary-light)' }} />;
    }
    return <CopilotChatButton />;
  };

  return (
    <Tooltip
      title={tooltipTitle}
      placement="top"
      disableHoverListener={!tooltipTitle}
      PopperProps={{
        sx: tooltipTitle ? { zIndex: DRAWER_Z_INDEX + 1 } : undefined,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 12],
            },
          },
        ],
      }}
    >
      <Box
        component="span"
        display="inline-flex"
        onClick={onClick}
        className={classes.buttonContainer}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={() => ({
          backgroundColor: disabled
            ? 'var(--buyer-copilot-disabled)'
            : buttonsStyles?.backgroundColor || 'var(--buyer-copilot-enabled)',
          '&:hover': {
            transform: disabled ? 'none' : 'scale(1.05)',
          },
        })}
      >
        {renderChatIcon()}
      </Box>
    </Tooltip>
  );
};

export default BuyerCopilotButton;
